import React, { useState, useMemo, useEffect } from 'react';

type CategoryId = 'length' | 'weight' | 'temp' | 'volume';

interface CategoryConfig {
  id: CategoryId;
  label: string;
  units: string[];
  /** For non-temp: value per 1 base unit (first unit). E.g. 1 km = 1000 m → factors[1]=1000 */
  factors?: number[];
}

const CATEGORIES: CategoryConfig[] = [
  {
    id: 'length',
    label: 'Length',
    units: ['km', 'm', 'cm', 'mm', 'mile', 'yard', 'foot', 'inch'],
    factors: [1, 1000, 100000, 1000000, 0.621371192, 1093.6133, 3280.8399, 39370.07874],
  },
  {
    id: 'weight',
    label: 'Weight',
    units: ['kg', 'g', 'lb', 'oz'],
    factors: [1, 1000, 2.20462262, 35.2739619],
  },
  {
    id: 'temp',
    label: 'Temperature',
    units: ['°C', '°F', 'K'],
  },
  {
    id: 'volume',
    label: 'Volume',
    units: ['L', 'mL', 'US gal', 'US fl oz', 'UK gal', 'UK fl oz'],
    factors: [1, 1000, 0.264172052, 33.8140227, 0.219969248, 35.1950797],
  },
];

function convertValue(
  category: CategoryConfig,
  value: number,
  fromIndex: number,
  toIndex: number
): number {
  if (category.id === 'temp') {
    const toCelsius = (v: number, i: number) => {
      if (i === 0) return v; // °C
      if (i === 1) return (v - 32) * (5 / 9); // °F
      return v - 273.15; // K
    };
    const fromCelsius = (c: number, i: number) => {
      if (i === 0) return c;
      if (i === 1) return c * (9 / 5) + 32;
      return c + 273.15;
    };
    const celsius = toCelsius(value, fromIndex);
    return fromCelsius(celsius, toIndex);
  }
  if (!category.factors || category.factors.length !== category.units.length) return value;
  const base = value / category.factors[fromIndex];
  if (!Number.isFinite(base)) return 0;
  return base * category.factors[toIndex];
}

function formatResult(num: number): string {
  if (!Number.isFinite(num)) return '—';
  if (num === 0) return '0';
  const abs = Math.abs(num);
  if (abs >= 1e6 || (abs < 0.0001 && abs > 0)) return num.toExponential(4);
  if (abs >= 1000 || (abs < 0.01 && abs >= 0.0001)) return num.toFixed(2);
  if (abs >= 1 || (abs < 1 && abs >= 0.01)) return num.toFixed(4);
  return num.toFixed(6);
}

/** Normalize unit for slug: "°C" → "c", "US gal" → "us-gal" */
function unitToSlug(u: string): string {
  return u.replace(/°/g, '').toLowerCase().replace(/\s+/g, '-');
}

/** Parse programmatic SEO slug e.g. "cm-to-inch" → category + from/to indices */
function parseUnitSlug(
  slug: string | undefined,
  categories: CategoryConfig[]
): { categoryId: CategoryId; fromIndex: number; toIndex: number } | null {
  if (!slug) return null;
  const parts = slug.toLowerCase().split('-to-');
  if (parts.length !== 2) return null;
  const [fromPart, toPart] = parts;
  for (const cat of categories) {
    const fromIdx = cat.units.findIndex((u) => unitToSlug(u) === fromPart);
    const toIdx = cat.units.findIndex((u) => unitToSlug(u) === toPart);
    if (fromIdx !== -1 && toIdx !== -1)
      return { categoryId: cat.id, fromIndex: fromIdx, toIndex: toIdx };
  }
  return null;
}

interface UnitConverterToolProps {
  slug?: string;
}

const UnitConverterTool: React.FC<UnitConverterToolProps> = ({ slug }) => {
  const parsed = parseUnitSlug(slug, CATEGORIES);
  const [categoryId, setCategoryId] = useState<CategoryId>(parsed?.categoryId ?? 'length');
  const [fromUnit, setFromUnit] = useState(parsed?.fromIndex ?? 0);
  const [toUnit, setToUnit] = useState(parsed?.toIndex ?? 1);
  const [value, setValue] = useState('1');

  const category = useMemo(
    () => CATEGORIES.find((c) => c.id === categoryId) ?? CATEGORIES[0],
    [categoryId]
  );

  useEffect(() => {
    const p = parseUnitSlug(slug, CATEGORIES);
    if (p) {
      setCategoryId(p.categoryId);
      setFromUnit(p.fromIndex);
      setToUnit(p.toIndex);
    }
  }, [slug]);

  const num = parseFloat(value);
  const validInput = !Number.isNaN(num) && Number.isFinite(num);

  const result = useMemo(() => {
    if (!validInput) return null;
    const fromIdx = Math.min(fromUnit, category.units.length - 1);
    const toIdx = Math.min(toUnit, category.units.length - 1);
    return convertValue(category, num, fromIdx, toIdx);
  }, [category, num, validInput, fromUnit, toUnit]);

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value as CategoryId;
    setCategoryId(id);
    setFromUnit(0);
    setToUnit(1);
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
        <select
          value={categoryId}
          onChange={handleCategoryChange}
          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 bg-white"
        >
          {CATEGORIES.map((c) => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
          <input
            type="number"
            step="any"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="0"
            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">From unit</label>
          <select
            value={fromUnit}
            onChange={(e) => setFromUnit(Number(e.target.value))}
            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 bg-white"
          >
            {category.units.map((u, i) => (
              <option key={i} value={i}>{u}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">To unit</label>
          <select
            value={toUnit}
            onChange={(e) => setToUnit(Number(e.target.value))}
            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 bg-white"
          >
            {category.units.map((u, i) => (
              <option key={i} value={i}>{u}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-teal-50 rounded-xl p-6 border border-teal-100">
        <div className="text-2xl font-bold text-teal-700">
          {result != null ? `${formatResult(result)} ${category.units[Math.min(toUnit, category.units.length - 1)]}` : 'Enter a value'}
        </div>
      </div>
    </div>
  );
};

export default UnitConverterTool;
